import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/treasury`;

interface Snapshot { id: number; bank_account: string; entity_code: string; currency: string; snapshot_date: string; opening_balance: number; inflows: number; outflows: number; closing_balance: number; usd_equivalent?: number; fx_rate?: number; fx_status?: 'IDENTITY' | 'OK' | 'UNAVAILABLE'; source: string; }
interface Position { total_balance: number; total_balance_native_sum?: number; functional_currency?: string; accounts: Snapshot[]; account_count: number; intraday_projection: { opening: number; expected_inflows: number; expected_outflows: number; projected_eod: number }; }
interface ReconSummary { total_lines: number; matched: number; unmatched: number; match_rate: number; unmatched_amount: number; }

export default function CashPosition() {
  const { entity } = useGlobalEntity();
  const [position, setPosition] = useState<Position | null>(null);
  const [recon, setRecon] = useState<ReconSummary | null>(null);
  const [history, setHistory] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('position');
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ bank_account: '', entity_code: 'USA', opening_balance: '', inflows: '', outflows: '', closing_balance: '' });

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, reconRes, histRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/cash-position`, entity)),
        authFetch(withEntityParam(`${API}/reconciliation/summary`, entity)),
        authFetch(withEntityParam(`${API}/cash-position/history?days=30`, entity)),
      ]);
      if (posRes.ok) setPosition(await posRes.json());
      if (reconRes.ok) setRecon(await reconRes.json());
      if (histRes.ok) { const d = await histRes.json(); setHistory(d.snapshots || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    try {
      const payload = { ...form, opening_balance: parseFloat(form.opening_balance) || 0, inflows: parseFloat(form.inflows) || 0, outflows: parseFloat(form.outflows) || 0, closing_balance: parseFloat(form.closing_balance) || 0 };
      const res = await authFetch(`${API}/cash-position`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Snapshot recorded', true); setShowAdd(false); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleAutoRecon = async () => {
    const res = await authFetch(`${API}/reconciliation/auto`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); showToast(`Reconciliation: ${d.matched || d.exact_matches || 0} matched`, true); load(); }
    else showToast('Reconciliation failed', false);
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const fxBadgeStyle = (status?: string): React.CSSProperties => ({
    fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
    background: status === 'UNAVAILABLE' ? 'var(--negative-bg)'
              : status === 'IDENTITY'    ? 'var(--bg-subtle)'
              : 'var(--positive-bg)',
    color: status === 'UNAVAILABLE' ? 'var(--negative)'
         : status === 'IDENTITY'    ? 'var(--text-secondary)'
         : 'var(--positive)',
  });

  const positionColumns: Column[] = [
    { key: 'bank_account', label: 'Account', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v || 'DEFAULT'}</span> },
    { key: 'entity_code', label: 'Entity', render: (v: string) => v || '-' },
    { key: 'currency', label: 'Ccy' },
    { key: 'opening_balance', label: 'Opening', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'inflows', label: 'Inflows', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'outflows', label: 'Outflows', align: 'right', render: (v: number) => <span style={{ color: 'var(--negative)' }}>{formatAmount(v)}</span> },
    { key: 'closing_balance', label: 'Closing (Native)', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    {
      key: 'usd_equivalent', label: 'USD Eq.', align: 'right',
      render: (v: number | undefined, row: Snapshot) => (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
          <span style={{ fontWeight: 600 }}>{formatAmount(v ?? row.closing_balance)}</span>
          {row.fx_status && row.fx_status !== 'IDENTITY' && (
            <span style={fxBadgeStyle(row.fx_status)} title={row.fx_status === 'UNAVAILABLE'
              ? 'No FX rate available -- showing native value untranslated. Upload current FX rates.'
              : `Translated at rate ${row.fx_rate?.toFixed(4) || '?'}`}>
              {row.fx_status === 'UNAVAILABLE' ? 'NO FX' : `@${row.fx_rate?.toFixed(2) || '?'}`}
            </span>
          )}
        </span>
      ),
    },
    { key: 'source', label: 'Source', render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v}</span> },
  ];

  const historyColumns: Column[] = [
    { key: 'snapshot_date', label: 'Date', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'bank_account', label: 'Account', render: (v: string) => v || '-' },
    { key: 'opening_balance', label: 'Opening', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'inflows', label: 'Inflows', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'outflows', label: 'Outflows', align: 'right', render: (v: number) => <span style={{ color: 'var(--negative)' }}>{formatAmount(v)}</span> },
    { key: 'closing_balance', label: 'Closing', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
  ];

  return (
    <PageLayout
      title="Treasury"
      subtitle="Cash position, intraday projection, and bank reconciliation"
      actions={[
        { label: 'Auto-Reconcile', onClick: handleAutoRecon, variant: 'secondary' },
        { label: '+ Snapshot', onClick: () => setShowAdd(true) },
      ]}
    >
      {position && (
        <KpiGrid
          columns={6}
          items={[
            { label: `Total Balance (${position.functional_currency || 'USD'})`, value: formatAmountCompact(position.total_balance), color: 'var(--accent)' },
            { label: 'Accounts', value: position.account_count },
            { label: 'Expected Inflows', value: formatAmountCompact(position.intraday_projection.expected_inflows), color: 'var(--positive)' },
            { label: 'Expected Outflows', value: formatAmountCompact(position.intraday_projection.expected_outflows), color: 'var(--negative)' },
            { label: 'Projected EOD', value: formatAmountCompact(position.intraday_projection.projected_eod), color: 'var(--accent)' },
            { label: 'Recon Rate', value: recon ? `${recon.match_rate}%` : '-', color: 'var(--positive)' },
          ]}
        />
      )}

      {/* Cash Position by Account Bar Chart -- uses USD-equivalent so
          SAR / BRL / USD accounts can be visually compared on one axis */}
      {position && position.accounts.length > 0 && (() => {
        const accts = position.accounts.slice(0, 10);
        const valOf = (a: Snapshot) => a.usd_equivalent ?? a.closing_balance;
        const maxVal = Math.max(...accts.map(a => Math.abs(valOf(a))), 1);
        const barW = 400 / accts.length;
        return (
          <div style={{ marginBottom: 20, padding: '16px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase' }}>
              Cash Position by Account ({position.functional_currency || 'USD'} equivalent)
            </div>
            <svg width="100%" height={140} viewBox={`0 0 400 140`} preserveAspectRatio="xMidYMid meet">
              {accts.map((a, i) => {
                const v = valOf(a);
                const h = (Math.abs(v) / maxVal) * 100;
                return (
                  <g key={i}>
                    <rect
                      x={i * barW + 2} y={110 - h}
                      width={barW - 4} height={h}
                      fill={v >= 0 ? 'var(--accent)' : 'var(--negative)'}
                      rx={3}
                    />
                    <text x={i * barW + barW / 2} y={125} textAnchor="middle" fontSize={8} fill="var(--text-secondary)">
                      {(a.bank_account || a.entity_code || 'Acct').slice(0, 8)}
                    </text>
                    <text x={i * barW + barW / 2} y={106 - h} textAnchor="middle" fontSize={7} fill="var(--text-primary)" fontWeight={600}>
                      {Math.abs(v) >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toFixed(0)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        );
      })()}

      <TabBar
        tabs={[
          { key: 'position', label: 'Current Position' },
          { key: 'history', label: 'History' },
          { key: 'recon', label: 'Reconciliation' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'position' ? (
        <DataTable
          columns={positionColumns}
          rows={position?.accounts || []}
          loading={loading}
          emptyMessage="No cash position data. Record a snapshot to start."
        />
      ) : tab === 'history' ? (
        <DataTable
          columns={historyColumns}
          rows={history}
          loading={loading}
          emptyMessage="No history data."
          rowKey="id"
        />
      ) : (
        loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</div>
        ) : recon ? (
          <KpiGrid
            columns={5}
            items={[
              { label: 'Total Lines', value: recon.total_lines },
              { label: 'Matched', value: recon.matched, color: 'var(--positive)' },
              { label: 'Unmatched', value: recon.unmatched, color: 'var(--negative)' },
              { label: 'Match Rate', value: `${recon.match_rate}%`, color: 'var(--accent)' },
              { label: 'Unmatched Amount', value: formatAmountCompact(recon.unmatched_amount), color: 'var(--negative)' },
            ]}
          />
        ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>No reconciliation data.</div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Record Cash Snapshot" footer={<><button onClick={() => setShowAdd(false)} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button><button onClick={handleAdd} style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Record</button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Bank Account" value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })} style={inputStyle} />
            <input placeholder="Entity Code" value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Opening Balance" type="number" value={form.opening_balance} onChange={e => setForm({ ...form, opening_balance: e.target.value })} style={inputStyle} />
            <input placeholder="Closing Balance" type="number" value={form.closing_balance} onChange={e => setForm({ ...form, closing_balance: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Inflows" type="number" value={form.inflows} onChange={e => setForm({ ...form, inflows: e.target.value })} style={inputStyle} />
            <input placeholder="Outflows" type="number" value={form.outflows} onChange={e => setForm({ ...form, outflows: e.target.value })} style={inputStyle} />
          </div>
        </div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
